import { useState } from "react";
import { supabase } from "../lib/supabaseClient.js";

interface EditCropHealthReportModalProps {
  open: boolean;
  onClose: () => void;
  report: any; // You can type this better according to your schema
  onReportUpdated: () => void;
}

export default function EditCropHealthReportModal({
  open,
  onClose,
  report,
  onReportUpdated,
}: EditCropHealthReportModalProps) {
  const [status, setStatus] = useState(report?.status || "");
  const [notes, setNotes] = useState(report?.notes || "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update state when report prop changes
  // (if you keep modal mounted between reports)
  // Otherwise, you can ignore this effect
  // useEffect(() => {
  //   setStatus(report?.status || "");
  //   setNotes(report?.notes || "");
  //   setImageFile(null);
  //   setError(null);
  // }, [report]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    let image_url = report.image_url;

    if (imageFile) {
      // Upload new image to Supabase Storage
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${report.id}_${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase
        .storage
        .from("crop-health-images")
        .upload(fileName, imageFile, { upsert: true });

      if (uploadError) {
        setError("Image upload failed: " + uploadError.message);
        setSaving(false);
        return;
      }

      // Get public URL
      const { data: publicUrlData } = supabase
        .storage
        .from("crop-health-images")
        .getPublicUrl(fileName);
      image_url = publicUrlData.publicUrl;
    }

    // Update the report in DB
    const { error: updateError } = await supabase
      .from("crop_health_reports")
      .update({ status, notes, image_url })
      .eq("id", report.id);

    if (updateError) {
      setError("Failed to update report: " + updateError.message);
      setSaving(false);
      return;
    }

    setSaving(false);
    onReportUpdated();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black bg-opacity-40">
      <div className="relative w-full max-w-md p-6 bg-white rounded-lg shadow-lg">
        <button
          className="absolute text-xl text-gray-400 top-3 right-3 hover:text-gray-700"
          onClick={onClose}
          disabled={saving}
          aria-label="Close"
        >
          &times;
        </button>
        <h2 className="mb-4 text-lg font-semibold">Edit Crop Health Report</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block mb-1 font-medium">
              Status
              <select
                className="block w-full px-2 py-1 mt-1 border border-gray-300 rounded"
                value={status}
                onChange={e => setStatus(e.target.value)}
                required
                disabled={saving}
              >
                <option value="">Select status</option>
                <option value="Healthy">Healthy</option>
                <option value="Needs Attention">Needs Attention</option>
                <option value="Diseased">Diseased</option>
                <option value="Pest Detected">Pest Detected</option>
              </select>
            </label>
          </div>
          <div>
            <label className="block mb-1 font-medium">
              Notes
              <textarea
                className="block w-full px-2 py-1 mt-1 border border-gray-300 rounded"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                disabled={saving}
              />
            </label>
          </div>
          <div>
            <label className="block mb-1 font-medium">
              Image
              <input
                type="file"
                accept="image/*"
                className="block mt-1"
                onChange={handleImageChange}
                disabled={saving}
              />
            </label>
            {report.image_url && !imageFile && (
              <img
                src={report.image_url}
                alt="Current"
                className="mt-2 border rounded max-h-32"
              />
            )}
            {imageFile && (
              <div className="mt-2 text-xs text-gray-500">{imageFile.name}</div>
            )}
          </div>
          {error && <div className="text-red-600">{error}</div>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 font-semibold text-white bg-blue-600 rounded hover:bg-blue-700"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}